import React, { useContext, useEffect, useState } from "react";

import { Error } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import useDebouncedEffect from "../../../../helpers/hooks/use-debounced-effect";
import { UrlParamTypes } from "../../../../types/org-display";
import { GetUsersListParams, getOrgUserList } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

interface RemoveUserProps {
  userId: string;
  userFirstName: string;
  userLastName: string;
  handleAssigneeId: (assigneeId: string) => void;
  handleRemoveUserBtn: (isDisabled: boolean) => void;
  roleId: string;
}

const RemoveUser = ({
  userId,
  userFirstName,
  userLastName,
  handleAssigneeId,
  handleRemoveUserBtn,
  roleId,
}: RemoveUserProps) => {
  const theme = useTheme();
  const { regionCode } = useParams<UrlParamTypes>();
  const [orgUsersList, setOrgUsersList] = useState<User[]>([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [assigneeInput, setAssigneeInput] = useState("");

  const { showError } = useContext(SnackbarContext);

  const getResults = (query: string) => {
    setIsLoading(true);
    const payload: GetUsersListParams = {
      organizationId: roleId,
      pageSize: 100,
      offset: 0,
      sortDirection: SortDirection.ASC,
      sortType: UserSortType.FIRST_NAME,
      query: query,
      userStatus: UserStatus.UNSPECIFIED,
    };

    getOrgUserList(payload, callOptionsHeaders(regionCode, roleId))
      .then((res) => {
        const filteredList = res.users.filter(
          (resultUser) => resultUser.id !== userId,
        );
        setOrgUsersList(filteredList);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSetAssigneeId = (value: string | User | null) => {
    if (!value) {
      return;
    }
    if (typeof value === "string") {
      return setAssigneeId(value);
    }
    if (value && typeof value === "object") {
      return setAssigneeId(value.id);
    }
  };

  const renderAssigneeInput = () => {
    return (
      <Box>
        <Autocomplete
          autoHighlight
          autoComplete
          options={orgUsersList}
          loading={isLoading}
          onChange={(event, value, reason) => {
            if (reason === "clear") {
              handleRemoveUserBtn(true);
            }
            if (reason === "selectOption") {
              handleRemoveUserBtn(false);
              handleSetAssigneeId(value);
            }
          }}
          onInputChange={(event, value) => {
            setAssigneeInput(value);
          }}
          getOptionLabel={(option: User) =>
            `${option.firstName} ${option.lastName} - ${option.email}`
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography>
                  {option.firstName} {option.lastName}{" "}
                </Typography>
                <Typography variant="caption">{option.email}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Assignee"
              name="assignee"
              variant="outlined"
              margin="normal"
              inputProps={{
                ...params.inputProps,
                "data-testid": "autocomplete-assignee-field",
              }}
            />
          )}
        />
      </Box>
    );
  };

  useEffect(() => {
    handleAssigneeId(assigneeId);
    // handleAssigneeId function not memoized in parent, so may have unintended side effects to include in deps
    // eslint-disable-next-line
  }, [assigneeId]);

  useEffect(() => {
    if (assigneeId) {
      handleRemoveUserBtn(false);
    }

    if (!assigneeId) {
      handleRemoveUserBtn(true);
    }
    // eslint-disable-next-line
  }, [assigneeId]);

  useDebouncedEffect(
    () => {
      getResults(assigneeInput);
    },
    500,
    [assigneeInput, setAssigneeInput],
  );

  return (
    <>
      <Grid container direction="row" spacing={1} style={{ minWidth: 350 }}>
        <Grid item xs={12} container direction="column">
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.secondary.main}
            marginBottom={3}
          >
            <Grid container alignItems="center">
              <Grid item xs={2} container justifyContent="center">
                <Error fontSize="large" />
              </Grid>
              <Grid item xs={10} style={{ paddingRight: "20px" }}>
                <p>
                  You are about to remove{" "}
                  <b>
                    {userFirstName} {userLastName}
                  </b>{" "}
                  from this organisation. The user will be hard deleted.
                  Otherwise, their user document will be <b> deleted </b> and
                  the customer can continue to login to their other
                  organisations.
                </p>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {renderAssigneeInput()}
        </Grid>
      </Grid>
    </>
  );
};

export default RemoveUser;
